/**
 * User Entitlements Modal - Manage User Permissions
 *
 * CRITICAL: Follows Tim-Combo Pattern (MANDATORY)
 * - Uses useStatus() hook for authentication state
 * - Uses useEffect for side effects
 * - No onSuccess callbacks, uses onUpdate prop for parent notification
 * - Error_Message from local state (component-specific errors)
 *
 * This component allows administrators to manage user entitlements:
 * - View existing entitlements
 * - Add new entitlements (client/company access)
 * - Update entitlement permissions (Admin, Approver, Executive flags)
 * - Remove entitlements
 *
 * Standards: .equilateral-standards/frontend-development/tim_combo_frontend_auth_pattern.md
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Label, TextInput, Checkbox, Badge, Alert, Spinner, Table } from 'flowbite-react';
import { HiPlus, HiTrash } from 'react-icons/hi';
import {
  User,
  UserEntitlement,
  getUserEntitlements,
  createUserEntitlement,
  updateUserEntitlements,
  deleteUserEntitlement,
  isSuccessResponse,
} from '../../services/api/users';

interface UserEntitlementsModalProps {
  show: boolean;
  onClose: () => void;
  user: User;
  onUpdate: () => void;  // Tim-Combo Pattern: Callback to notify parent of changes
}

const UserEntitlementsModal: React.FC<UserEntitlementsModalProps> = ({
  show,
  onClose,
  user,
  onUpdate,
}) => {
  // Local state for entitlements management
  const [entitlements, setEntitlements] = useState<UserEntitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state for adding new entitlement
  const [newEntitlement, setNewEntitlement] = useState({
    Client_ID: '',
    Company_ID: '',
    Admin: false,
    Approver: false,
    Executive: false,
  });

  const fetchEntitlements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getUserEntitlements(user.Email_Address);

      if (isSuccessResponse(response)) {
        setEntitlements(response.data.Records);
      } else {
        setError(response.message || 'Failed to load entitlements');
      }
    } catch (err) {
      console.error('Error fetching entitlements:', err);
      setError(err instanceof Error ? err.message : 'Failed to load entitlements');
    } finally {
      setLoading(false);
    }
  }, [user.Email_Address]);

  // Tim-Combo Pattern: useEffect for side effects (data fetching)
  useEffect(() => {
    if (show) {
      fetchEntitlements();
    }
  }, [show, fetchEntitlements]);

  const handleAddEntitlement = async () => {
    setError(null);

    try {
      const response = await createUserEntitlement({
        Email_Address: user.Email_Address,
        Client_ID: newEntitlement.Client_ID,
        Company_ID: newEntitlement.Company_ID,
        Admin: newEntitlement.Admin,
        Approver: newEntitlement.Approver,
        Executive: newEntitlement.Executive,
      });

      if (isSuccessResponse(response)) {
        // Tim-Combo Pattern: Update state, then refetch
        await fetchEntitlements();
        setShowAddForm(false);
        resetNewEntitlementForm();
        // Notify parent component of changes
        onUpdate();
      } else {
        setError(response.message || 'Failed to add entitlement');
      }
    } catch (err) {
      console.error('Error adding entitlement:', err);
      setError(err instanceof Error ? err.message : 'Failed to add entitlement');
    }
  };

  const handleUpdateEntitlement = async (entitlement: UserEntitlement, updates: Partial<UserEntitlement>) => {
    setError(null);

    try {
      const response = await updateUserEntitlements({
        Email_Address: entitlement.Email_Address,
        Client_ID: entitlement.Client_ID,
        Company_ID: entitlement.Company_ID,
        Admin: updates.Admin ?? entitlement.Admin,
        Approver: updates.Approver ?? entitlement.Approver,
        Executive: updates.Executive ?? entitlement.Executive,
      });

      if (isSuccessResponse(response)) {
        // Tim-Combo Pattern: Update state, then refetch
        await fetchEntitlements();
        // Notify parent component of changes
        onUpdate();
      } else {
        setError(response.message || 'Failed to update entitlement');
      }
    } catch (err) {
      console.error('Error updating entitlement:', err);
      setError(err instanceof Error ? err.message : 'Failed to update entitlement');
    }
  };

  const handleDeleteEntitlement = async (entitlement: UserEntitlement) => {
    setError(null);

    if (!window.confirm(`Remove access to ${entitlement.Company_Name || entitlement.Company_ID}?`)) {
      return;
    }

    try {
      const response = await deleteUserEntitlement(
        entitlement.Email_Address,
        entitlement.Client_ID,
        entitlement.Company_ID
      );

      if (isSuccessResponse(response)) {
        // Tim-Combo Pattern: Update state, then refetch
        await fetchEntitlements();
        // Notify parent component of changes
        onUpdate();
      } else {
        setError(response.message || 'Failed to delete entitlement');
      }
    } catch (err) {
      console.error('Error deleting entitlement:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete entitlement');
    }
  };

  const resetNewEntitlementForm = () => {
    setNewEntitlement({
      Client_ID: '',
      Company_ID: '',
      Admin: false,
      Approver: false,
      Executive: false,
    });
  };

  const handleClose = () => {
    setShowAddForm(false);
    resetNewEntitlementForm();
    setError(null);
    onClose();
  };

  return (
    <Modal show={show} onClose={handleClose} size="4xl">
      <Modal.Header>
        Manage Entitlements - {user.Email_Address}
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          {/* Error display */}
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* User Info */}
          <div className="pb-4 border-b">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>User:</strong> {user.User_Display_Name || user.Email_Address}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Client:</strong> {user.Client_ID}
            </p>
          </div>

          {/* Add New Entitlement Form */}
          {showAddForm ? (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h3 className="text-lg font-semibold mb-4">Add New Entitlement</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="clientId">Client ID *</Label>
                  <TextInput
                    id="clientId"
                    value={newEntitlement.Client_ID}
                    onChange={(e) =>
                      setNewEntitlement({ ...newEntitlement, Client_ID: e.target.value })
                    }
                    placeholder="Enter client ID"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="companyId">Company ID *</Label>
                  <TextInput
                    id="companyId"
                    value={newEntitlement.Company_ID}
                    onChange={(e) =>
                      setNewEntitlement({ ...newEntitlement, Company_ID: e.target.value })
                    }
                    placeholder="Enter company ID"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Permissions</Label>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="admin"
                        checked={newEntitlement.Admin}
                        onChange={(e) =>
                          setNewEntitlement({ ...newEntitlement, Admin: e.target.checked })
                        }
                      />
                      <Label htmlFor="admin">Admin</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="approver"
                        checked={newEntitlement.Approver}
                        onChange={(e) =>
                          setNewEntitlement({ ...newEntitlement, Approver: e.target.checked })
                        }
                      />
                      <Label htmlFor="approver">Approver</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="executive"
                        checked={newEntitlement.Executive}
                        onChange={(e) =>
                          setNewEntitlement({ ...newEntitlement, Executive: e.target.checked })
                        }
                      />
                      <Label htmlFor="executive">Executive</Label>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddEntitlement}
                    disabled={!newEntitlement.Client_ID || !newEntitlement.Company_ID}
                  >
                    Add Entitlement
                  </Button>
                  <Button
                    color="gray"
                    onClick={() => {
                      setShowAddForm(false);
                      resetNewEntitlementForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button onClick={() => setShowAddForm(true)} size="sm">
              <HiPlus className="mr-2 h-4 w-4" />
              Add New Entitlement
            </Button>
          )}

          {/* Entitlements List */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Current Entitlements</h3>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Spinner size="lg" />
              </div>
            ) : entitlements.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                No entitlements found. Add one above to get started.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <Table.HeadCell>Client / Company</Table.HeadCell>
                    <Table.HeadCell>Admin</Table.HeadCell>
                    <Table.HeadCell>Approver</Table.HeadCell>
                    <Table.HeadCell>Executive</Table.HeadCell>
                    <Table.HeadCell>Granted</Table.HeadCell>
                    <Table.HeadCell>Actions</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {entitlements.map((entitlement) => (
                      <Table.Row
                        key={`${entitlement.Client_ID}-${entitlement.Company_ID}`}
                        className="bg-white dark:border-gray-700 dark:bg-gray-800"
                      >
                        <Table.Cell>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {entitlement.Company_Name || entitlement.Company_ID}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {entitlement.Client_Name || entitlement.Client_ID}
                            </p>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={entitlement.Admin}
                            onChange={(e) =>
                              handleUpdateEntitlement(entitlement, { Admin: e.target.checked })
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={entitlement.Approver}
                            onChange={(e) =>
                              handleUpdateEntitlement(entitlement, { Approver: e.target.checked })
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Checkbox
                            checked={entitlement.Executive}
                            onChange={(e) =>
                              handleUpdateEntitlement(entitlement, { Executive: e.target.checked })
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {new Date(entitlement.Granted_At).toLocaleDateString()}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => handleDeleteEntitlement(entitlement)}
                          >
                            <HiTrash className="h-4 w-4" />
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserEntitlementsModal;
